if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        queueMicrotask(() => {
          const selectedOptions = this.getSelectedOptions();
          this.applyGalleryFilter(selectedOptions);
          const sectionId = this.id.replace(/^MediaGallery-/, '');
          const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
          this.applyModalFilter(modalContent, selectedOptions);
        });
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      getSelectedOptions() {
        const options = {};
        this.closest('product-info')?.querySelectorAll('variant-selects [name^="options["]').forEach((input) => {
          if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
          const match = input.name.match(/^options\[(.*)\]$/);
          if (match) options[match[1]] = input.value;
        });
        return options;
      }

      applyGalleryFilter(selectedOptions = {}) {
        const normalizedOptions = Object.fromEntries(
          Object.entries(selectedOptions).map(([name, value]) => [this.normalizeGalleryValue(name), this.normalizeGalleryValue(value)])
        );
        const isVisible = (item) => this.isGalleryItemVisible(item, normalizedOptions);

        this.querySelectorAll('[data-media-id]').forEach((item) => {
          item.hidden = !isVisible(item);
        });
        this.querySelectorAll('[data-target]').forEach((item) => {
          item.hidden = !isVisible(item);
        });

        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (activeMedia?.hidden) {
          const fallback = this.elements.viewer.querySelector('[data-media-id]:not([hidden])');
          if (fallback) this.setActiveMedia(fallback.dataset.mediaId, false);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        this.updateVisibleCounter();
      }

      applyModalFilter(modalContent, selectedOptions = {}) {
        if (!modalContent) return;
        const normalizedOptions = Object.fromEntries(
          Object.entries(selectedOptions).map(([name, value]) => [this.normalizeGalleryValue(name), this.normalizeGalleryValue(value)])
        );
        modalContent.querySelectorAll(':scope > [data-media-id]').forEach((item) => {
          item.hidden = !this.isGalleryItemVisible(item, normalizedOptions);
        });
      }

      isGalleryItemVisible(item, normalizedOptions) {
        if (item.dataset.variantMedia === 'true') return true;
        const rules = this.parseGalleryRules(item.dataset.mediaAlt || '');
        if (rules === null) return false;
        return rules.every(([name, values]) => values.includes(normalizedOptions[name]));
      }

      updateVisibleCounter(activeMedia = null) {
        const visibleItems = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]:not([hidden])'));
        const current = activeMedia || this.elements.viewer.querySelector('li[data-media-id].is-active:not([hidden])');
        const currentIndex = visibleItems.length ? Math.max(visibleItems.indexOf(current), 0) + 1 : 0;
        const currentCounter = this.querySelector('.slider-counter--current');
        const totalCounter = this.querySelector('.slider-counter--total');
        if (currentCounter) currentCounter.textContent = String(currentIndex);
        if (totalCounter) totalCounter.textContent = String(visibleItems.length);
      }

      parseGalleryRules(alt) {
        const marker = alt.match(/(?:^|\s)gallery:([^|]+)/i);
        if (!marker) return [];
        const expression = marker[1].trim();
        if (!expression || /^all$/i.test(expression)) return [];

        const rules = expression.split(';').map((rule) => {
          const separator = rule.indexOf('=');
          if (separator <= 0 || separator === rule.length - 1) return null;
          const values = rule
            .slice(separator + 1)
            .split('~')
            .map((value) => this.normalizeGalleryValue(value))
            .filter(Boolean);
          if (!values.length) return null;
          return [this.normalizeGalleryValue(rule.slice(0, separator)), values];
        });
        return rules.some((rule) => rule === null) ? null : rules;
      }

      normalizeGalleryValue(value) {
        return String(value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateVisibleCounter(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateVisibleCounter(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
